import type { CoreOptions } from '@adyen/adyen-web/dist/types/core/types';
import { useEffect, useMemo, useState } from 'react';
import type DropinElement from '@adyen/adyen-web/dist/types/components/Dropin/Dropin';
import { useNavigate } from 'react-router';
import type { AdyenPaymentSession, PaymentMethod } from '@jwp/ott-common/types/checkout';
import { getModule } from '@jwp/ott-common/src/modules/container';
import CheckoutController from '@jwp/ott-common/src/controllers/CheckoutController';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { createURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { useTranslation } from 'react-i18next';
import useEventCallback from '@jwp/ott-hooks-react/src/useEventCallback';

import Adyen from '../../components/Adyen/Adyen';
import { useAriaAnnouncer } from '../AnnouncementProvider/AnnoucementProvider';

type Props = {
  setUpdatingOrder: (loading: boolean) => void;
  onPaymentMethodChange: (paymentMethod: string) => void;
  paymentMethods: PaymentMethod[];
  selectedPaymentMethod: PaymentMethod | undefined;
  onPayPalSubmit: () => void;
  paymentSuccessUrl: string;
  orderId?: number;
  offerId?: string;
  getCaptchaValue: () => Promise<string | undefined>;
};

export default function AdyenInitialPayment({
  setUpdatingOrder,
  onPaymentMethodChange,
  paymentSuccessUrl,
  selectedPaymentMethod,
  onPayPalSubmit,
  orderId,
  offerId,
  paymentMethods,
  getCaptchaValue,
}: Props) {
  const accountController = getModule(AccountController);
  const checkoutController = getModule(CheckoutController);
  const { t } = useTranslation(['account', 'error']);
  const announce = useAriaAnnouncer();

  const [error, setError] = useState<string>();
  const [session, setSession] = useState<AdyenPaymentSession>();

  const isSandbox = accountController.getSandbox();
  const navigate = useNavigate();

  useEffect(() => {
    const createSession = async () => {
      setUpdatingOrder(true);

      try {
        const session = await checkoutController.createAdyenPaymentSession(window.origin);

        setSession(session);
      } catch (error: unknown) {
        if (error instanceof Error) {
          setError(error.message);
        }
      }

      setUpdatingOrder(false);
    };

    createSession();
  }, [setUpdatingOrder, checkoutController]);

  const onSubmit = useEventCallback(async (state: AdyenEventData, handleAction: DropinElement['handleAction']) => {
    if (!state.isValid) return;

    try {
      setUpdatingOrder(true);
      setError(undefined);

      if (orderId === undefined) {
        setError(t('error:adyen_order_unknown'));
        return;
      }

      const captchaValue = await getCaptchaValue();

      const returnUrl = createURL(window.location.href, {
        u: 'finalize-payment',
        orderId: orderId,
        offerId,
        paymentSuccessUrl,
      });
      const result = await checkoutController.initialAdyenPayment(state.data.paymentMethod, returnUrl, captchaValue);

      if ('action' in result) {
        handleAction(result.action);
      }

      await accountController.reloadSubscriptions({ retry: 10, offerId });
      announce(t('account:checkout.payment_success'), 'success');
      navigate(paymentSuccessUrl, { replace: true });
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      }
    }

    setUpdatingOrder(false);
  });

  const onAdditionalDetails = useEventCallback(async (state: AdyenAdditionalEventData) => {
    try {
      setUpdatingOrder(true);

      await checkoutController.finalizeAdyenPayment(state.data.details, orderId);

      announce(t('account:checkout.payment_success'), 'success');
      navigate(paymentSuccessUrl, { replace: true });
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
        setUpdatingOrder(false);
      }
    }
  });

  const adyenConfiguration: CoreOptions = useMemo(
    () => ({
      showPayButton: false,
      environment: isSandbox ? 'test' : 'live',
      clientKey: session?.clientKey,
      amount: session?.amount,
      paymentMethodsResponse: { paymentMethods: session?.paymentMethods || [] },
      paymentMethodsConfiguration: {
        card: {
          hasHolderName: true,
          holderNameRequired: true,
        },
      },
      onAdditionalDetails,
      onSubmit: (state: AdyenEventData, component: DropinElement) => onSubmit(state, component.handleAction),
      onError: (error: Error) => setError(error.message),
    }),
    [isSandbox, session?.clientKey, session?.amount, session?.paymentMethods, onAdditionalDetails, onSubmit],
  );

  return (
    <Adyen
      configuration={adyenConfiguration}
      error={error}
      onPaymentMethodChange={onPaymentMethodChange}
      paymentMethods={paymentMethods}
      selectedPaymentMethod={selectedPaymentMethod}
      onPayPalSubmit={onPayPalSubmit}
    />
  );
}
